// #18929 — single source of truth for the signed-in user's preferred display
// timezone (Settings → Account → Timezone). The saved preference used to reach
// no formatter, so every time rendered in the browser's zone. This module holds
// the chosen IANA zone and persists it to localStorage, so it is available
// synchronously on the next load — before the profile is re-fetched — and the
// central date formatter (./datetime) can read it everywhere.

const STORAGE_KEY = 'co.user.timezone';

function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Whether the runtime accepts `tz` as an IANA zone. */
export function isValidTimeZone(tz: string): boolean {
  if (!tz) return false;
  try {
    // Throws RangeError for an unknown zone.
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function readStored(): string | undefined {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && isValidTimeZone(v) ? v : undefined;
  } catch {
    return undefined;
  }
}

let current: string | undefined = readStored();

/**
 * The user's preferred IANA zone, or the browser's own zone when none is set
 * (so unconfigured sessions behave exactly as before).
 */
export function getUserTimeZone(): string {
  return current || browserZone();
}

/**
 * Record the user's preferred zone. An empty or invalid value clears it, so
 * formatting falls back to the browser's zone. Persisted to localStorage.
 */
export function setUserTimeZone(tz?: string | null): void {
  if (tz && isValidTimeZone(tz)) {
    current = tz;
    try {
      localStorage.setItem(STORAGE_KEY, tz);
    } catch {
      /* storage unavailable — the in-memory value still applies this session */
    }
  } else {
    current = undefined;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
}
